import { CONFIG } from "../constants.js";
import { Particle } from "../entities/Particle.js";
import { SpawnManager } from "../managers/SpawnManager.js";
import { ScoringManager } from "../managers/ScoringManager.js";
/** @typedef {import('../game.js').AIHorizon} AIHorizon */

/**
 * Factory helpers for spawning & acquiring pooled game objects.
 * All methods rely on pools / RNG & performance tuning parameters stored on the game instance.
 */
export const GameFactories = {
  /**
   * Acquire a bullet from the pool, positioned at the player's cannon.
   * @param {AIHorizon} game
   * @returns {import('../entities/Bullet.js').Bullet}
   */
  createBullet(game) {
    const player = /** @type {any} */ (game.player);
    const pool = /** @type {any} */ (game.bulletPool);
    const upgraded = ScoringManager.bulletUpgradeActive(game);
    const bw =
      upgraded && (CONFIG.BULLET.WIDTH_UPGRADED || 0) > 0
        ? CONFIG.BULLET.WIDTH_UPGRADED
        : CONFIG.BULLET.WIDTH;
    const bh =
      upgraded && (CONFIG.BULLET.HEIGHT_UPGRADED || 0) > 0
        ? CONFIG.BULLET.HEIGHT_UPGRADED
        : CONFIG.BULLET.HEIGHT;
    const bx = player.x + (player.width - bw) / 2 + CONFIG.BULLET.SPAWN_OFFSET;
    return pool.acquire(bx, player.y, bw, bh, game.bulletSpeed, upgraded ? "upgraded" : "normal");
  },
  /**
   * Spawn an explosion entity & its particle cloud subject to performance budget.
   * Particle grays are drawn from the RNG (keeps seeded runs reproducible) then snapped to the
   * discrete palette so the shared particle sprite cache stays small.
   * @param {AIHorizon} game
   * @param {number} x
   * @param {number} y
   */
  createExplosion(game, x, y) {
    const rng = /** @type {any} */ (game.rng);
    const particlePool = /** @type {any} */ (game.particlePool);
    const explosionPool = /** @type {any} */ (game.explosionPool);
    const particles = /** @type {any[]} */ (game.particles);
    const explosions = /** @type {any[]} */ (game.explosions);
    const perfMult = /** @type {number} */ (game._performanceParticleMultiplier || 1);
    const budget = Number.isFinite(game._particleBudget)
      ? /** @type {number} */ (game._particleBudget)
      : Number.POSITIVE_INFINITY;
    const particleCount = Math.max(0, Math.round(CONFIG.EXPLOSION.PARTICLE_COUNT * perfMult));
    for (let i = 0; i < particleCount; i++) {
      if (particles.length >= budget) break;
      const vx = (rng.nextFloat() - 0.5) * CONFIG.EXPLOSION.PARTICLE_SPEED_VAR;
      const vy = (rng.nextFloat() - 0.5) * CONFIG.EXPLOSION.PARTICLE_SPEED_VAR;
      const size =
        rng.range(0, CONFIG.EXPLOSION.PARTICLE_SIZE_VARIATION) + CONFIG.EXPLOSION.PARTICLE_SIZE_MIN;
      const gray = Particle.quantizeGray(
        rng.range(CONFIG.EXPLOSION.PARTICLE_GRAY_MIN, CONFIG.EXPLOSION.PARTICLE_GRAY_MAX)
      );
      particles.push(
        particlePool.acquire(
          x,
          y,
          vx,
          vy,
          CONFIG.EXPLOSION.PARTICLE_LIFE,
          CONFIG.EXPLOSION.PARTICLE_LIFE,
          size,
          Particle.sparkColor(gray)
        )
      );
    }
    explosions.push(
      explosionPool.acquire(
        x - CONFIG.EXPLOSION.OFFSET,
        y - CONFIG.EXPLOSION.OFFSET,
        CONFIG.EXPLOSION.SIZE,
        CONFIG.EXPLOSION.SIZE,
        CONFIG.EXPLOSION.LIFE,
        CONFIG.EXPLOSION.LIFE
      )
    );
  },
  /**
   * Create a star (delegated to SpawnManager for central rules).
   * @param {AIHorizon} game
   */
  createStar(game) {
    return SpawnManager.createStar(/** @type {any} */ (game));
  },
  /**
   * Create an asteroid (delegated to SpawnManager).
   * @param {AIHorizon} game
   */
  createAsteroid(game) {
    return SpawnManager.createAsteroid(/** @type {any} */ (game));
  },
};
